import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { CHECKOUT_PROBABILITY_LINE_ITEM_PROPERTY_KEY } from "@redotech/redo-model/checkouts/segments";
import { memo, useContext, useEffect } from "react";
import { log } from "../../monitoring/log";
import { ExtensionShopifyClientContext } from "../../shopify";
import { CheckoutProbabilityContext } from "./checkout-probability-context";

export const CheckoutProbabilityTracker = memo(
  function CheckoutProbabilityTracker() {
    const checkoutProbabilityContext = useContext(CheckoutProbabilityContext);
    const extensionShopifyClient = useRequiredContext(
      ExtensionShopifyClientContext,
    );

    const updateLineItemAttributes = useHandler(async () => {
      try {
        if (!checkoutProbabilityContext?.cumulativeConversionProbability) {
          return;
        }

        if (!extensionShopifyClient.updateLineItemAttributes) {
          console.warn("updateLineItemAttributes is not available");
          return;
        }

        const probabilityScore = Math.round(
          checkoutProbabilityContext.cumulativeConversionProbability * 100,
        );
        const previousProbabilityScore =
          checkoutProbabilityContext.previousCumulativeConversionProbability
            ? Math.round(
                checkoutProbabilityContext.previousCumulativeConversionProbability *
                  100,
              )
            : null;

        log("probabilityScore", probabilityScore);

        // Only update if the probability has changed
        if (previousProbabilityScore === probabilityScore) {
          log("probabilityScore is the same, skipping");
          return;
        }

        // Get current cart
        const cart = await extensionShopifyClient.cartGet();
        if (!cart?.items || cart.items.length === 0) {
          return;
        }

        // Update all line items with the new probability score in a single batch
        const updates = cart.items.map((lineItem) => {
          const existingProperties = lineItem.properties ?? {};
          return {
            lineItemId: String(lineItem.id),
            attributes: [
              ...Object.entries(existingProperties).map(([key, value]) => ({
                key,
                value,
              })),
              {
                key: CHECKOUT_PROBABILITY_LINE_ITEM_PROPERTY_KEY,
                value: probabilityScore.toString(),
              },
            ],
          };
        });

        await extensionShopifyClient.updateLineItemAttributes?.(updates);
      } catch (error) {
        console.error("Error in checkout probability tracker:", error);
      }
    });

    useEffect(() => {
      // Update line item attributes when context values change
      void updateLineItemAttributes();
    }, [
      checkoutProbabilityContext?.cumulativeConversionProbability,
      updateLineItemAttributes,
    ]);

    // This component doesn't render anything visible
    return null;
  },
);
